import { getSeekId } from '@seek/adv-account-selection';
import type { HirerAuthProvider } from '@seek/hirer-auth-react';
import type { ComponentProps } from 'react';

import { getAuthenticationLevel } from 'src/app/utils/environment';

import {
  setSeekIdForLocalDev,
  setAdcentreAdvertiserIdForLocalDev,
} from './utils';

const devSeekIdMappings: Record<string, string> = {
  /*
   * 1000 hirers verified/unverified variations
   */
  '1': '13', // AU hirer: Bus. address unverified & email both verified
  '2': '13', // AU hirer: Bus. address unverified & email verified
  '1000': '10', // AU hirer: Bus. address & email unverified
  '1100': '11', // AU hirer: Bus. address verified & email unverified
  '1200': '12', // AU hirer: Bus. address unverified & email verified
  '1300': '13', // AU hirer: Bus. address & email both verified
  '1400': '14', // NZ hirer: Bus. address & email both verified
  '1500': '15', // AU hirer: SSO verified / AdCenter unverified (Silent verification)
  '1600': '16', // AU hirer: Pay per outcome product (CPQA experiment)
  '1700': '18', // AU hirer: 4 choice Ad Ladder (Mid Tier experiment)
  '1900': '19', // AU hirer: Pay&Post V2 experiment
  /*
   * 2000 hirers with custom features enabled
   */
  '2000': '20', // AU hirer: On credit hold
  '2100': '21', // AU hirer: Private advertiser with Branded ad
  '2200': '22', // AU hirer: Hide price enabled
  '2300': '23', // NZ hirer: Hide price enabled
  '2400': '24', // AU hirer: Private advertiser with classic ad
  '2500': '25', // AU hirer: Hidden salary enabled
  /*
   * 3000 hirers on contracts or budgets
   */
  '3000': '30', // AU hirer: Has branded budget
  '3100': '31', // AU hirer: Has contract balance
  '3200': '32', // AU hirer: Has contract balance and access to new Pay And Post Page
  /*
   * 4000 hirers with hirer type based features. e.g gov/recruiter
   */
  '4000': '40', // AU hirer: Linkout to external website enabled
  '4100': '41', // AU hirer: Has agents
  '4200': '42', // AU hirer: Government
  '4300': '43', // AU hirer: Government with set ad expiry (edit flow)
  '4400': '44', // AU hirer: Linkout to SEEK site with RR questionnaire
  '4500': '45', // AU hirer: Linkout to SEEK site with NO RR questionnaire
  /*
   * 5000 hirers from different nations (SEEK and non-SEEK) e.g. AU, MY, UK
   */
  '5000': '50', // ID hirer: Bus. address & email both verified
  '5100': '51', // UK hirer: Bus. address & email both verified
  '5300': '53', // HK hirer: Bus. address & email both verified
  /*
   * 6000 hirers for Talent Search approvals
   */
  '6100': '61', // AU hirer: TalentSearch approved hirer
  '6200': '62', // NZ hirer: TalentSearch approved hirer
};

export const getHirerAuthProviderProps = (): Omit<
  ComponentProps<typeof HirerAuthProvider>,
  'children'
> => {
  const authenticationLevel = getAuthenticationLevel();

  return {
    authenticationLevel,
    ...(authenticationLevel === 'Mocked' && {
      mockOptions: getMockOptions(),
    }),
  };
};

const getMockOptions = (): ComponentProps<
  typeof HirerAuthProvider
>['mockOptions'] => {
  if (getAuthenticationLevel() !== 'Mocked') {
    return;
  }

  // if a hirer account id has been set in local storage already, prefer that over the default.
  // However, if a value is supplied from the querystring, we should prefer that over the local storage value.
  // This confusingly brittle logic is another strong reason why we should be removing the dependency in the first place.
  const searchParams = new URLSearchParams(
    window.location.search.toLowerCase(),
  );
  const querystringHirerAccountId = searchParams.get('hireraccountid');
  const previouslySetHirerAccountId = getSeekId();
  const hirerAccountId =
    querystringHirerAccountId ?? previouslySetHirerAccountId ?? '1';

  // we still need to set the legacy adcentre advertiser id to support role requirements and potentially other legacy code
  const adcentreAdvertiserId = devSeekIdMappings[hirerAccountId];

  setSeekIdForLocalDev(hirerAccountId);
  setAdcentreAdvertiserIdForLocalDev(adcentreAdvertiserId);

  return {
    authenticated: true,
    identity: {
      ...(hirerAccountId && {
        advertiserId: hirerAccountId,
      }),
    },
  };
};
